import axios from 'axios';
import { TPgErrorResponse } from './types';

/** Error body of the gateway, if the error is an HTTP error with a JSON body. */
export const getPgErrorBody = (error: unknown): TPgErrorResponse | null => {
	if (!axios.isAxiosError(error)) return null;
	const data = error.response?.data as unknown;
	if (!data || typeof data !== 'object' || data instanceof Blob) return null;
	return data as TPgErrorResponse;
};

/** HTTP status of a failed gateway call. */
export const getPgErrorStatus = (error: unknown): number | null =>
	axios.isAxiosError(error) ? (error.response?.status ?? null) : null;

/** `errorCode` of the gateway (`quote_changed`, `payment_disputed`, `run_in_progress`, …). */
export const getPgErrorCode = (error: unknown): string | null =>
	getPgErrorBody(error)?.errorCode ?? null;

/** Text to show to the operator: the gateway `message`, else the fallback. */
export const getPgErrorMessage = (error: unknown, fallback: string): string => {
	const body = getPgErrorBody(error);
	if (body?.message) return body.message;
	if (getPgErrorStatus(error) === 403) {
		return 'Access denied: the paymentgateway_admin role is required.';
	}
	return fallback;
};

/**
 * The same for calls made with `responseType: 'blob'` (receipt download): the JSON error body
 * arrives as a Blob and has to be read first.
 */
export const readPgErrorMessage = async (error: unknown, fallback: string): Promise<string> => {
	if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
		try {
			const text = await error.response.data.text();
			const body = JSON.parse(text) as TPgErrorResponse;
			if (body?.message) return body.message;
		} catch {
			/* not a JSON body */
		}
	}
	return getPgErrorMessage(error, fallback);
};
